import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Input,
  Button,
  Select,
  Tag,
  Space,
  Typography,
  Tooltip,
  Popconfirm,
  Badge,
  Empty,
  Row,
  Col,
  Checkbox,
  Pagination,
  Alert,
} from 'antd';
import { 
  InfoCircleOutlined, 
  WarningOutlined, 
  CloseCircleOutlined, 
  ReloadOutlined,
  EyeOutlined,
  EditFilled,
  DeleteFilled,
  PlusOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';
import AlertSilenceIconButton from './AlertSilenceIconButton';
import {
  GRAFANA_PLATFORM,
  getAlertDisplayDuration,
  getAlertDisplayExpression,
  getAlertDisplayReceiver,
  getAlertPlatformLabel,
  isAmpAlert,
  resolveAmpRuntimeStatus,
} from '../utils/ampAlertHelpers';

const { Text, Paragraph } = Typography;
const { Option } = Select;

const getSeverityConfig = (severity, isDark) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return {
        color: '#ff4d4f',
        bg: isDark ? '#2d1b1b' : '#fff2f0',
        iconBg: isDark ? '#3d2525' : '#ffccc7',
        icon: <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />,
      };
    case 'warning':
      return {
        color: '#fa8c16',
        bg: isDark ? '#2d241b' : '#fff7e6',
        iconBg: isDark ? '#3d2f25' : '#ffe7ba',
        icon: <WarningOutlined style={{ fontSize: 20, color: '#fa8c16' }} />,
      };
    case 'info':
      return {
        color: '#1890ff',
        bg: isDark ? '#1c2833' : '#e6f7ff',
        iconBg: isDark ? '#253340' : '#bae7ff',
        icon: <InfoCircleOutlined style={{ fontSize: 20, color: '#1890ff' }} />,
      };
    default:
      return {
        color: isDark ? '#adbac7' : '#8c8c8c',
        bg: isDark ? '#21262d' : '#fafafa',
        iconBg: isDark ? '#30363d' : '#f0f0f0',
        icon: <InfoCircleOutlined style={{ fontSize: 20, color: isDark ? '#adbac7' : '#8c8c8c' }} />,
      };
  }
};

const getStatusConfig = (status, isDark) => {
  switch (status?.toLowerCase()) {
    case 'firing':
      return {
        color: '#ff4d4f',
        bg: isDark ? '#2d1b1b' : '#fff2f0',
        label: 'Firing',
        dotColor: '#ff4d4f',
        textColor: isDark ? '#ff6b6b' : '#ff4d4f',
      };
    case 'pending':
      return {
        color: '#fa8c16',
        bg: isDark ? '#2d241b' : '#fff7e6',
        label: 'Pending',
        dotColor: '#fa8c16',
        textColor: isDark ? '#ffa940' : '#fa8c16',
      };
    case 'suppressed':
    case 'silenced':
      return {
        color: '#faad14',
        bg: isDark ? '#2a241c' : '#fffbe6',
        label: 'Suppressed',
        dotColor: '#faad14',
        textColor: isDark ? '#ffc53d' : '#d48806',
      };
    case 'normal':
    case 'inactive':
      return {
        color: '#52c41a',
        bg: isDark ? '#1c2a1c' : '#f6ffed',
        label: 'Normal',
        dotColor: '#52c41a',
        textColor: isDark ? '#73d13d' : '#52c41a',
      };
    default:
      return {
        color: isDark ? '#adbac7' : '#8c8c8c',
        bg: isDark ? '#21262d' : '#fafafa',
        label: 'Unknown',
        dotColor: isDark ? '#adbac7' : '#8c8c8c',
        textColor: isDark ? '#adbac7' : '#8c8c8c',
      };
  }
};

const getAlertStatus = (alert) => {
  const silenced =
    alert.isSilenced ||
    Boolean(alert.silenceId) ||
    alert.state === 'suppressed' ||
    alert.state === 'silenced';

  if (isAmpAlert(alert)) {
    const deployStatus = String(alert.ampDeployStatus || '').toUpperCase();
    if (deployStatus === 'FAILED') return 'normal';
    if (deployStatus === 'PENDING_DEPLOYMENT') return 'pending';
    if (silenced) return 'suppressed';
    const runtimeStatus = resolveAmpRuntimeStatus(alert);
    if (runtimeStatus === 'suppressed' || runtimeStatus === 'silenced') return 'suppressed';
    if (runtimeStatus === 'firing') return 'firing';
    if (runtimeStatus === 'pending') return 'pending';
    return 'normal';
  }

  if (silenced) return 'suppressed';
  if (alert.state === 'suppressed' || alert.state === 'silenced') return 'suppressed';
  if (alert.state === 'pending') return 'pending';
  if (alert.state === 'firing') return 'firing';
  if (alert.state === 'normal') return 'normal';

  if (alert.status === 'suppressed' || alert.status === 'silenced' || alert.status?.state === 'suppressed') return 'suppressed';
  if (alert.status === 'pending' || alert.status?.state === 'pending') return 'pending';
  if (alert.status === 'firing' || alert.status?.state === 'firing') return 'firing';

  const isSomePending = Array.isArray(alert.data) && alert.data.some(d => d.state === 'pending' || d.status === 'pending' || d.status?.state === 'pending');
  if (isSomePending || alert.labels?.state === 'pending') {
    return 'pending';
  }
  const isSomeFiring = Array.isArray(alert.data) && alert.data.some(d => d.state === 'firing' || d.status === 'firing' || d.status?.state === 'firing');
  if (isSomeFiring || alert.labels?.state === 'firing') {
    return 'firing';
  }
  return 'normal';
};

const getAlertSeverity = (alert) => {
  const severity =
    alert.severity ||
    alert.labels?.severity ||
    alert.data?.[0]?.labels?.severity ||
    'warning';
  return String(severity).toLowerCase();
};

const matchesAlertSearch = (alert, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const title = String(alert.title || '').toLowerCase();
  const alertId = String(alert.id || '').toLowerCase();
  const alertName = String(alert.labels?.alertname || '').toLowerCase();
  return (
    title.includes(q) ||
    alertName.includes(q) ||
    alertId.includes(q) ||
    alertId.endsWith(`#${q}`) ||
    getAlertDisplayExpression(alert).toLowerCase().includes(q) ||
    String(alert.ruleGroup || '').toLowerCase().includes(q)
  );
};

const findAlertByName = (alerts, name) => {
  const q = String(name || '').trim().toLowerCase();
  if (!q) return null;

  const exactMatch = (alert) => {
    const title = String(alert.title || '').toLowerCase();
    const alertName = String(alert.labels?.alertname || '').toLowerCase();
    const alertId = String(alert.id || '').toLowerCase();
    return title === q || alertName === q || alertId.endsWith(`#${q}`);
  };

  return alerts.find((alert) => exactMatch(alert)) || alerts.find((alert) => matchesAlertSearch(alert, q));
};

const getAmpDeployStatusConfig = (status, isDark) => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'FAILED') {
    return { color: '#ff4d4f', label: 'Deploy Failed' };
  }
  if (normalized === 'PENDING_DEPLOYMENT') {
    return { color: '#fa8c16', label: 'Pending Deploy' };
  }
  if (normalized === 'ACTIVE') {
    return { color: '#52c41a', label: 'Active' };
  }
  return { color: isDark ? '#adbac7' : '#8c8c8c', label: normalized || 'Unknown' };
};

const StatCard = ({ value, label, color, isActive, onClick, showDot, isDark }) => (
  <div
    onClick={onClick}
    style={{
      background: isDark ? '#161b22' : '#fff',
      borderRadius: 12,
      padding: '20px 24px',
      cursor: 'pointer',
      border: isActive ? `2px solid ${color || '#1890ff'}` : `2px solid ${isDark ? '#30363d' : (color || '#e8e8e8')}`,
      boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
      transition: 'all 0.2s',
      textAlign: 'center',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <span style={{ fontSize: 28, fontWeight: 700, color: color || (isDark ? '#e6edf3' : '#262626') }}>
        {value}
      </span>
      {showDot && value > 0 && (
        <span style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          animation: 'pulse 2s infinite',
        }} />
      )}
    </div>
    <div style={{ fontSize: 14, color: isDark ? '#adbac7' : '#8c8c8c', marginTop: 4 }}>{label}</div>
  </div>
);

const AlertCard = ({
  alert,
  isDark,
  isExpanded,
  isSelected,
  onToggleExpand,
  onToggleSelect,
  onUnsilenceAlert,
  onSilenceAlert,
  onEditAlert,
  onDeleteAlert,
  onAskAIDebugger,
  isViewer,
}) => {
  const [expressionCopied, setExpressionCopied] = useState(false);
  const copyResetTimerRef = useRef(null);
  const status = getAlertStatus(alert);
  const severity = getAlertSeverity(alert);
  const expression = getAlertDisplayExpression(alert);
  const displayDuration = getAlertDisplayDuration(alert);
  const displayReceiver = getAlertDisplayReceiver(alert);
  const severityConfig = getSeverityConfig(severity, isDark);
  const statusConfig = getStatusConfig(status, isDark);
  const platformLabel = getAlertPlatformLabel(alert.platform);
  const ampDeployConfig = isAmpAlert(alert)
    ? getAmpDeployStatusConfig(alert.ampDeployStatus, isDark)
    : null;
  const isSilenced =
    alert.isSilenced ||
    Boolean(alert.silenceId) ||
    alert.state === 'suppressed' ||
    alert.state === 'silenced';

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      setExpressionCopied(false);
    }
  }, [isExpanded]);

  const handleCopyExpression = async () => {
    if (!expression) return;
    try {
      await navigator.clipboard.writeText(expression);
      setExpressionCopied(true);
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = setTimeout(() => {
        setExpressionCopied(false);
        copyResetTimerRef.current = null;
      }, 2000);
    } catch {
      setExpressionCopied(false);
    }
  };

  const getBorderColor = () => {
    const severityColors = {
      critical: '#ff4d4f',
      warning: '#fa8c16',
      info: '#1890ff',
    };

    return severityColors[severity?.toLowerCase()] || '#e8e8e8';
  };

  return (
    <div
      style={{
        background: isDark ? '#161b22' : '#fff',
        borderRadius: 12,
        marginBottom: 12,
        borderLeft: `4px solid ${getBorderColor()}`,
        border: isDark ? '1px solid #30363d' : undefined,
        boxShadow: isSelected ? '0 0 0 2px #1890ff' : '0 1px 2px rgba(0,0,0,0.03)',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(alert.id)}
            style={{ marginTop: 4 }}
          />

          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: severityConfig.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {severityConfig.icon}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4, color: isDark ? '#e6edf3' : '#262626' }}>
              {alert.title}
            </Text>
            {(alert.id || alert.alert_id) && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  fontFamily: 'monospace',
                  display: 'block',
                  marginBottom: 8,
                  color: isDark ? '#8b949e' : '#8c8c8c',
                }}
              >
                ID: {alert.id || alert.alert_id}
              </Text>
            )}

            <Space size={8} wrap>
              {isAmpAlert(alert) && (
                <Tag color="volcano" style={{ borderRadius: 4, fontWeight: 500 }}>
                  {platformLabel}
                </Tag>
              )}

              {isAmpAlert(alert) && alert.ruleGroup && (
                <Tag style={{ borderRadius: 4 }}>{alert.ruleGroup}</Tag>
              )}

              {ampDeployConfig && (
                <Tooltip title={alert.ampDeployError || undefined}>
                  <Tag
                    style={{
                      borderRadius: 4,
                      color: ampDeployConfig.color,
                      borderColor: ampDeployConfig.color,
                      background: isDark ? '#21262d' : '#fff',
                    }}
                  >
                    {ampDeployConfig.label}
                  </Tag>
                </Tooltip>
              )}

              <Tag
                style={{
                  background: statusConfig.bg,
                  border: 'none',
                  borderRadius: 4,
                  padding: '2px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: statusConfig.dotColor,
                    display: 'inline-block',
                  }}
                />
                <span style={{ color: statusConfig.textColor || statusConfig.color, fontWeight: 500 }}>
                  {statusConfig.label}
                </span>
              </Tag>

              <Tag
                style={{
                  background: severityConfig.bg,
                  border: 'none',
                  borderRadius: 4,
                  color: severityConfig.color,
                  fontWeight: 500,
                }}
              >
                <span style={{ color: severityConfig.color }}>{severity}</span>
              </Tag>

              {alert.notificationChannels?.[0] && (
                <span style={{ fontSize: 13, color: isDark ? '#adbac7' : '#8c8c8c', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {alert.notificationChannels[0]}
                </span>
              )}
            </Space>
          </div>

          <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 100 }}>
            {alert.triggerCount > 0 && (
              <div style={{ fontSize: 12, color: isDark ? '#6e7681' : '#bfbfbf', marginTop: 2 }}>
                {alert.triggerCount} triggers
              </div>
            )}
          </div>

          <Space size={4}>
            <Tooltip title="View details">
              <Button
                icon={<EyeOutlined />}
                type="text"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleExpand(alert.id);
                }}
              />
            </Tooltip>
            <Tooltip title={isSilenced ? 'Unsilence' : 'Silence'}>
            <span>
            <AlertSilenceIconButton
              isSilenced={isSilenced}
              onClick={() =>
                isSilenced
                  ? onUnsilenceAlert?.(alert)
                  : onSilenceAlert?.(alert)
              }
              disabled={isViewer}
            />
            </span>
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                icon={<EditFilled />}
                type="text"
                size="small"
                onClick={() => onEditAlert?.(alert)}
                disabled={isViewer}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this alert?"
              onConfirm={() => onDeleteAlert?.(alert)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button
                  icon={<DeleteFilled />}
                  type="text"
                  danger
                  size="small"
                  disabled={isViewer}
                />
              </Tooltip>
            </Popconfirm>
            {onAskAIDebugger && (
              <Tooltip
                title={
                  isAmpAlert(alert)
                    ? "Ask AI to investigate this AMP alert (Prometheus metrics & logs)"
                    : "Ask AI why this alert is happening"
                }
                overlayInnerStyle={{ maxWidth: 260, overflow: 'hidden' }}
                overlayStyle={{ maxWidth: 260, overflow: 'visible' }}
              >
                <Button
                  icon={<LinkOutlined />}
                  type="text"
                  size="small"
                  onClick={() => onAskAIDebugger(alert)}
                />
              </Tooltip>
            )}
          </Space>
        </div>

        {isExpanded && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${isDark ? '#30363d' : '#f0f0f0'}` }}>
            {expression && (
            <div style={{ background: isDark ? '#0d1117' : '#fafafa', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: isDark ? '#adbac7' : undefined }}>
                  Rule Expression
                </Text>
                <Button
                  type="link"
                  size="small"
                  onClick={handleCopyExpression}
                  style={{
                    padding: 0,
                    height: 'auto',
                    fontSize: 12,
                    color: expressionCopied
                      ? (isDark ? '#3fb950' : '#52c41a')
                      : (isDark ? '#58a6ff' : undefined),
                  }}
                >
                  {expressionCopied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <code style={{
                display: 'block',
                fontSize: 13,
                wordBreak: 'break-all',
                color: isDark ? '#adbac7' : '#595959',
                fontFamily: 'Monaco, Consolas, monospace',
              }}>
                {expression}
              </code>
            </div>
            )}
            {(displayDuration || displayReceiver) && (
              <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c' }}>
                {displayDuration ? `Duration: ${displayDuration}` : ''}
                {displayDuration && displayReceiver ? ' · ' : ''}
                {displayReceiver ? `Receiver: ${displayReceiver}` : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EnhancedAlertRulesView = ({
  alerts = [],
  loading = false,
  ampSyncing = false,
  alertCapabilities = null,
  initialSearchQuery = '',
  initialPlatformFilter = 'all',
  initialExpandAlertName = '',
  onRefresh,
  onCreateAlert,
  onEditAlert,
  onDeleteAlert,
  onSilenceAlert,
  onUnsilenceAlert,
  onAskAIDebugger,
  isViewer = false,
}) => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const alertsScrollRef = useRef(null);

  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  useEffect(() => {
    if (!initialExpandAlertName || !alerts.length) return;
    const match = findAlertByName(alerts, initialExpandAlertName);
    if (match?.id) {
      setExpandedAlert(match.id);
    }
  }, [alerts, initialExpandAlertName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterSeverity]);

  const toggleExpandAlert = useCallback((alertId) => {
    const contentScroll = document.querySelector('.content-scroll');
    const contentScrollTop = contentScroll?.scrollTop ?? 0;
    const listScrollTop = alertsScrollRef.current?.scrollTop ?? 0;

    setExpandedAlert((prev) => (prev === alertId ? null : alertId));

    requestAnimationFrame(() => {
      if (contentScroll) contentScroll.scrollTop = contentScrollTop;
      if (alertsScrollRef.current) alertsScrollRef.current.scrollTop = listScrollTop;
    });
  }, []);

  // Filter alerts
  const filteredAlerts = useMemo(() => {
    return alerts.filter(alert => {
      const matchesSearch = matchesAlertSearch(alert, searchQuery);
      
      const status = getAlertStatus(alert);
      const matchesStatus = filterStatus === 'all' || status === filterStatus;
      
      const severity = getAlertSeverity(alert);
      const matchesSeverity = filterSeverity === 'all' || severity === filterSeverity;
      
      return matchesSearch && matchesStatus && matchesSeverity;
    });
  }, [alerts, searchQuery, filterStatus, filterSeverity]);

  const paginatedAlerts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAlerts.slice(start, start + pageSize);
  }, [filteredAlerts, currentPage, pageSize]);

  // Calculate stats
  const stats = useMemo(() => ({
    total: alerts.length,
    firing: alerts.filter(a => getAlertStatus(a) === 'firing').length,
    pending: alerts.filter(a => getAlertStatus(a) === 'pending').length,
    suppressed: alerts.filter(a => getAlertStatus(a) === 'suppressed').length,
    normal: alerts.filter(a => getAlertStatus(a) === 'normal').length,
  }), [alerts]);

  // Toggle alert selection
  const toggleSelectAlert = (id) => {
    setSelectedAlerts(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Header - static */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexShrink: 0,
        }}
      >
        <div>
          <Text strong style={{ fontSize: 22, color: isDark ? '#e6edf3' : '#262626' }}>Alert Rules</Text>
          <div style={{ color: isDark ? '#adbac7' : '#8c8c8c', fontSize: 14, marginTop: 4 }}>
            Manage your monitoring alerts
          </div>
          {ampSyncing && (
            <div style={{ marginTop: 6 }}>
              <Badge status="processing" text={<span style={{ color: isDark ? '#8b949e' : '#8c8c8c' }}>AMP syncing...</span>} />
            </div>
          )}
        </div>
        <Space size={12}>
          <Tooltip title="Refresh">
            <Button 
              onClick={onRefresh}
              loading={loading}
              icon={<ReloadOutlined />}
              style={{ 
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => onCreateAlert?.()}
            disabled={isViewer}
            style={{ 
              borderRadius: 8,
              background: '#1890ff',
              fontWeight: 500,
            }}
          >
            Create Alert
          </Button>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24, flexShrink: 0 }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <StatCard
            value={stats.total}
            label="Total Rules"
            isActive={filterStatus === 'all'}
            onClick={() => setFilterStatus('all')}
            isDark={isDark}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <StatCard
            value={stats.firing}
            label="Firing"
            color="#ff4d4f"
            isActive={filterStatus === 'firing'}
            onClick={() => setFilterStatus('firing')}
            showDot={true}
            isDark={isDark}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <StatCard
            value={stats.pending}
            label="Pending"
            color="#fa8c16"
            isActive={filterStatus === 'pending'}
            onClick={() => setFilterStatus('pending')}
            isDark={isDark}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <StatCard
            value={stats.suppressed}
            label="Suppressed"
            color="#faad14"
            isActive={filterStatus === 'suppressed'}
            onClick={() => setFilterStatus('suppressed')}
            isDark={isDark}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <StatCard
            value={stats.normal}
            label="Normal"
            color="#52c41a"
            isActive={filterStatus === 'normal'}
            onClick={() => setFilterStatus('normal')}
            isDark={isDark}
          />
        </Col>
      </Row>

      {alertCapabilities?.atGrafanaLimit && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, flexShrink: 0 }}
          message={`Grafana alert rule limit reached (${alertCapabilities.grafanaRuleCount}/${alertCapabilities.grafanaRuleLimit})`}
          description={
            alertCapabilities.canUseAmpForMetrics
              ? 'New metric alerts are placed in AMP automatically. Log and correlation alerts need Grafana capacity freed first.'
              : 'Delete unused Grafana rules before creating new ones, or ask an administrator to enable AMP for metric alerts.'
          }
        />
      )}
      {!alertCapabilities?.atGrafanaLimit && alertCapabilities?.nearGrafanaLimit && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, flexShrink: 0 }}
          message={`Grafana alert capacity is high (${alertCapabilities.grafanaRuleCount}/${alertCapabilities.grafanaRuleLimit})`}
          description="Consider pruning unused rules before the hard limit is reached."
        />
      )}

      {/* Search and Filters - static (stays visible) */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexShrink: 0 }}>
        <Input
          className="clair-alert-search-input"
          placeholder="Search alerts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ 
            flex: 1, 
            borderRadius: 10,
            height: 44,
            background: isDark ? '#0d1117' : undefined,
            borderColor: isDark ? '#30363d' : '#d9d9d9',
            color: isDark ? '#e6edf3' : undefined,
          }}
          allowClear
        />
        <Select
          value={filterSeverity}
          onChange={setFilterSeverity}
          style={{ 
            width: 160, 
            height: 44,
            background: isDark ? '#0d1117' : undefined,
            borderColor: isDark ? '#30363d' : undefined,
          }}
          dropdownStyle={{ 
            borderRadius: 8,
            background: isDark ? '#161b22' : undefined,
          }}
        >
          <Option value="all">All Severities</Option>
          <Option value="critical">Critical</Option>
          <Option value="warning">Warning</Option>
          <Option value="info">Info</Option>
        </Select>
        
        {selectedAlerts.length > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12, 
            paddingLeft: 16, 
            borderLeft: `1px solid ${isDark ? '#30363d' : '#e8e8e8'}` 
          }}>
            <Text type="secondary" style={{ fontSize: 13, color: isDark ? '#adbac7' : undefined }}>{selectedAlerts.length} selected</Text>
            <Button size="small" style={{ borderRadius: 6 }}>Silence All</Button>
            <Button size="small" danger style={{ borderRadius: 6 }}>Delete</Button>
          </div>
        )}
      </div>

      {/* Alert Cards - only this section scrolls */}
      <div
        ref={alertsScrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {loading && alerts.length === 0 ? (
          <div style={{
            background: isDark ? '#161b22' : '#fff',
            borderRadius: 12,
            padding: 48,
            textAlign: 'center',
            border: isDark ? '1px solid #30363d' : undefined,
          }}>
            <Badge status="processing" text={<span style={{ color: isDark ? '#adbac7' : '#8c8c8c' }}>Loading alert rules...</span>} />
          </div>
        ) : filteredAlerts.length > 0 ? (
          paginatedAlerts.map(alert => (
            <AlertCard
              key={`${alert.platform || GRAFANA_PLATFORM}-${alert.id || alert.title}`}
              alert={alert}
              isDark={isDark}
              isExpanded={expandedAlert === alert.id}
              isSelected={selectedAlerts.includes(alert.id)}
              onToggleExpand={toggleExpandAlert}
              onToggleSelect={toggleSelectAlert}
              onUnsilenceAlert={onUnsilenceAlert}
              onSilenceAlert={onSilenceAlert}
              onEditAlert={onEditAlert}
              onDeleteAlert={onDeleteAlert}
              onAskAIDebugger={onAskAIDebugger}
              isViewer={isViewer}
            />
          ))
        ) : (
          <div style={{ 
            background: isDark ? '#161b22' : '#fff', 
            borderRadius: 12, 
            padding: 48,
            textAlign: 'center',
            border: isDark ? '1px solid #30363d' : undefined,
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: isDark ? '#21262d' : '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              
            </div>
            <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4, color: isDark ? '#e6edf3' : undefined }}>
              No alerts found
            </Text>
            <Text type="secondary" style={{ color: isDark ? '#adbac7' : undefined }}>
              {searchQuery || filterStatus !== 'all' || filterSeverity !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first alert to get started'}
            </Text>
            {!searchQuery && filterStatus === 'all' && filterSeverity === 'all' && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={() => onCreateAlert?.()}
                  disabled={isViewer}
                >
                  Create Alert
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .clair-alert-search-input:hover {
          border-color: #4096ff !important;
        }

        .clair-alert-search-input:focus,
        .clair-alert-search-input-focused,
        .clair-alert-search-input.ant-input-focused,
        .clair-alert-search-input.ant-input-affix-wrapper-focused {
          border-color: #1677ff !important;
          box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.12) !important;
        }
      `}</style>
      {filteredAlerts.length > 0 && (
        <div style={{ flexShrink: 0, marginTop: 24, textAlign: 'right' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredAlerts.length}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger
            showTotal={(total) => <span style={{ color: isDark ? '#adbac7' : undefined }}>Total {total} alerts</span>}
          />
        </div>
      )}
    </div>
  );
};

export default EnhancedAlertRulesView;
